import { SEO } from '../components/common/SEO';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/ui/Button';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { businessInfo, operatorDisclosure } from '../utils/businessInfo';

const contactCards = [
    {
        icon: '💬',
        title: 'General Support',
        description: 'Questions about the app, features, or general inquiries',
        email: businessInfo.email,
    },
    {
        icon: '🔒',
        title: 'Privacy Inquiries',
        description: 'Data protection, privacy policy, or personal information requests',
        email: businessInfo.email,
    },
    {
        icon: '🛡️',
        title: 'Safety & Security',
        description: 'Report abuse, safety concerns, or security issues',
        email: businessInfo.email,
    },
    {
        icon: '🤝',
        title: 'Business Partnerships',
        description: 'Brand collaborations, business partnerships, or enterprise solutions',
        email: businessInfo.email,
    },
    {
        icon: '🎯',
        title: 'Marketing & PR',
        description: 'Press inquiries, media requests, or marketing collaborations',
        email: businessInfo.email,
    },
    {
        icon: '💼',
        title: 'Careers',
        description: 'Job opportunities, career inquiries, or talent acquisition',
        email: businessInfo.email,
    },
];

const responseItems = [
    { type: 'General Support', time: 'Within 24-48 hours' },
    { type: 'Privacy Inquiries', time: 'Within 72 hours' },
    { type: 'Safety Issues', time: 'Within 4-24 hours (urgent issues prioritized)' },
    { type: 'Business Inquiries', time: 'Within 3-5 business days' },
];

const faqItems = [
    {
        question: 'How do I report inappropriate content?',
        answer: `You can report content directly in the app by tapping the report button on any video or profile. For urgent safety concerns, email us at ${businessInfo.email}.`,
    },
    {
        question: 'How do I delete my account and data?',
        answer: `You can delete your account directly in the app settings, or contact us at ${businessInfo.email} for assistance with data deletion requests.`,
    },
    {
        question: 'How do I become a Swag Shots partner or creator?',
        answer: `Information about our creator programs is available in the app. For business partnerships, contact ${businessInfo.email}.`,
    },
    {
        question: "I'm having technical issues with the app. What should I do?",
        answer: `First, try updating to the latest version of the app. If issues persist, contact ${businessInfo.email} with details about your device and the problem you're experiencing.`,
    },
];

export const ContactPage: React.FC = () => {
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    const toggleFaq = (index: number) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    return (
        <Layout>
            <SEO
                title="Contact Us - Swag Shots"
                description="Get in touch with Swag Shots team. Contact us for support, partnerships, or general inquiries."
                url="https://www.swagshots.com/contact"
            />

            <div className="pt-24 pb-16">
                <div className="container-custom">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Contact Us</h1>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                            We're here to help! Choose the best way to reach our team.
                        </p>

                        {/* Support Contact Info */}
                        <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl p-6 max-w-2xl mx-auto border border-primary-200">
                            <div className="flex flex-col gap-2 text-left sm:flex-row sm:items-center sm:justify-between mb-4">
                                <h3 className="font-semibold text-gray-900">Swag Shots Support</h3>
                                <span className="text-sm text-gray-500 font-medium bg-white px-2 py-1 rounded border border-primary-100">
                                    Operated by {businessInfo.operatorName}
                                </span>
                            </div>
                            <p className="mb-4 text-left text-sm text-gray-700">{operatorDisclosure}</p>
                            <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 text-left">
                                <div className="flex items-center space-x-3">
                                    <span className="text-2xl">🏢</span>
                                    <span className="text-gray-800 font-medium">{businessInfo.operatorName}</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="text-2xl">📞</span>
                                    <a href={`tel:${businessInfo.phoneHref}`} className="text-primary-600 font-medium hover:text-primary-700">{businessInfo.phoneDisplay}</a>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="text-2xl">✉️</span>
                                    <a href={`mailto:${businessInfo.email}`} className="text-primary-600 font-medium hover:text-primary-700">{businessInfo.email}</a>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="text-2xl">🧾</span>
                                    <span className="text-gray-800 font-medium">GSTIN: {businessInfo.gstin}</span>
                                </div>
                                <div className="flex items-start space-x-3 md:col-span-2">
                                    <span className="text-2xl">📍</span>
                                    <span className="text-gray-800 font-medium">{businessInfo.registeredAddress}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Cards Grid */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
                        {contactCards.map((card, index) => (
                            <div
                                key={index}
                                className="bg-gray-50 p-6 rounded-xl border-2 border-gray-200 hover:border-primary-600 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg text-center group"
                            >
                                <div className="text-4xl mb-4">{card.icon}</div>
                                <h3 className="text-xl font-semibold text-primary-600 mb-3">{card.title}</h3>
                                <p className="text-gray-600 mb-4">{card.description}</p>
                                <a
                                    href={`mailto:${card.email}`}
                                    className="inline-block px-4 py-2 border-2 border-primary-600 text-primary-600 font-semibold rounded-lg hover:bg-primary-600 hover:text-white transition-colors"
                                >
                                    {card.email}
                                </a>
                            </div>
                        ))}
                    </div>

                    {/* Response Times */}
                    <div className="bg-gradient-to-br from-primary-600 to-primary-500 text-white rounded-xl p-8 mb-16 text-center">
                        <h2 className="text-3xl font-bold mb-4">Response Times</h2>
                        <p className="text-primary-100 mb-8">
                            We strive to respond to all inquiries as quickly as possible:
                        </p>
                        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 max-w-2xl mx-auto">
                            <div className="space-y-4">
                                {responseItems.map((item, index) => (
                                    <div key={index} className="flex justify-between items-center">
                                        <span className="font-semibold">{item.type}:</span>
                                        <span className="text-primary-100">{item.time}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* FAQ Section */}
                    <div className="max-w-4xl mx-auto mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
                        <div className="space-y-4">
                            {faqItems.map((item, index) => (
                                <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <button
                                        onClick={() => toggleFaq(index)}
                                        className="w-full bg-gray-50 px-6 py-4 text-left font-semibold text-gray-800 hover:bg-gray-100 transition-colors border-b border-gray-200"
                                    >
                                        {item.question}
                                    </button>
                                    {openFaq === index && (
                                        <div className="px-6 py-4 text-gray-600">
                                            {item.answer}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Bottom CTA */}
                    <div className="text-center">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">Need immediate assistance?</h3>
                        <p className="text-gray-600 mb-6">
                            For urgent safety concerns or critical technical issues, please use the in-app reporting features for
                            the fastest response.
                        </p>
                        <Link to="/#download">
                            <Button variant="primary" size="lg">
                                Download Swag Shots
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
